import TwilioVideoRoom from './TwilioVideoRoom';

import TelemedicineContext from '../../../context/telemedicine';
import TrackUtils from '../../../utils/track';
import { getDevices } from '../../../utils/video-room';

import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Video from 'twilio-video';

const RECONNECT_INTERVAL_MS = 5000;

const TwilioVideoRoomContainer = () => {
	const navigate = useNavigate();
	const { telemedicine } = useContext(TelemedicineContext);
	const [devices, setDevices] = useState([]);
	const [videoRoomState, setVideoRoomState] = useState({
		room: null,
		loading: false,
		previewTracks: []
	});

	const videoRoomStateRef = useRef(videoRoomState);
	const attemptedRef = useRef(false);
	const leavingRef = useRef(false);

	videoRoomStateRef.current = videoRoomState;

	const handleUpdateVideoRoomState = useCallback(newState => {
		setVideoRoomState(currentState => ({
			...currentState,
			...newState
		}));
	}, []);

	const onParticipantConnected = useCallback((participant, containerId) => {
		const doctorMediaContainer = document.getElementById(containerId);

		participant.on('trackDisabled', disabledTrack => {
			const tracks = TrackUtils.getTracks(participant).filter(track => (
				track.kind === disabledTrack.kind && track.name === disabledTrack.trackName
			));

			TrackUtils.detachTracks(tracks);
		});

		participant.on('trackEnabled', publication => TrackUtils.onTrackPublished(publication, doctorMediaContainer));
		participant.on('trackPublished', publication => TrackUtils.onTrackPublished(publication, doctorMediaContainer));
		participant.tracks.forEach(publication => TrackUtils.onTrackPublished(publication, doctorMediaContainer));
	}, []);

	const handleDisconnectRoom = useCallback(() => {
		leavingRef.current = true;

		if (videoRoomState.previewTracks.length) {
			videoRoomState.previewTracks.forEach(track => track.stop());
		}

		if (videoRoomState.room) {
			videoRoomState.room?.participants.forEach(participant => TrackUtils.detachTracks(TrackUtils.getTracks(participant)));
			TrackUtils.detachTracks(TrackUtils.getTracks(videoRoomState.room?.localParticipant));

			videoRoomState.room?.disconnect();
		}

		handleUpdateVideoRoomState({
			previewTracks: [],
			room: null
		});
	}, [videoRoomState, handleUpdateVideoRoomState]);

	const handleCreateListeners = useCallback(room => {
		navigator.mediaDevices.enumerateDevices().then(mediaDevices => setDevices(getDevices(mediaDevices)));

		room.participants.forEach(participant => onParticipantConnected(participant, 'doctor-tracks'));
		room.on('participantConnected', participant => onParticipantConnected(participant, 'doctor-tracks'));
		room.on('participantDisconnected', participant => TrackUtils.getTracks(participant).forEach(TrackUtils.detachTrack));

		room.on('disconnected', () => {
			if (leavingRef.current) {
				return;
			}

			handleUpdateVideoRoomState({ loading: false, room: null });
		});
	}, [onParticipantConnected, handleUpdateVideoRoomState]);

	const handleConnectRoom = useCallback(async info => {
		handleUpdateVideoRoomState({ loading: true });

		try {
			const connectionOptions = {
				name: info.room_name,
				tracks: await Video.createLocalTracks({
					audio: true,
					video: true
				})
			};

			const roomConnected = await Video.connect(info.token, connectionOptions);

			handleUpdateVideoRoomState({
				loading: false,
				room: roomConnected,
				previewTracks: connectionOptions.tracks
			});

			handleCreateListeners(roomConnected);

			TrackUtils.attachTracks(connectionOptions.tracks, document.getElementById('patient-tracks'));

			setTimeout(() => {
				const videoElement = document.querySelector('#patient-tracks video');

				videoElement.style.objectFit = 'cover';
			}, 400);
		} catch (error) {
			handleUpdateVideoRoomState({ loading: false, room: null });
		}
	}, [handleCreateListeners, handleUpdateVideoRoomState]);

	useEffect(() => {
		const shouldConnect = !leavingRef.current && !videoRoomState.loading && !videoRoomState.room;

		const timer = shouldConnect
			? setTimeout(() => {
				attemptedRef.current = true;

				handleConnectRoom({
					room_name: telemedicine.room_name,
					token: telemedicine.room_token
				});
			}, attemptedRef.current ? RECONNECT_INTERVAL_MS : 0)
			: null;

		return () => {
			if (timer) {
				clearTimeout(timer);
			}

			window.removeEventListener('beforeunload', handleDisconnectRoom);
			window.addEventListener('pagehide', handleDisconnectRoom);
		};
	}, [videoRoomState.loading, videoRoomState.room, telemedicine, handleConnectRoom, handleDisconnectRoom]);

	useEffect(() => {
		const handleStartVideoCall = info => {
			const current = videoRoomStateRef.current.room;

			if (leavingRef.current) {
				return;
			}

			if (current && current.state === 'connected') {
				return;
			}

			if (current) {
				current.disconnect();
			}

			handleConnectRoom({ room_name: info.room_name, token: info.token });
		};

		window.socket.on('telemedicine:start-video-call', handleStartVideoCall);

		return () => {
			window.socket.off('telemedicine:start-video-call', handleStartVideoCall);
		};
	}, [handleConnectRoom]);

	useEffect(() => {
		window.socket.on('telemedicine:room-closed', () => {
			handleDisconnectRoom();
			navigate('/telemedicine-finished');
		});

		window.addEventListener('beforeunload', handleDisconnectRoom);
		window.addEventListener('pagehide', handleDisconnectRoom);
	}, [navigate, handleDisconnectRoom]);

	return (
		<TwilioVideoRoom {...videoRoomState} devices={devices} />
	);
};

export default TwilioVideoRoomContainer;
